import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Language } from "./translations";
import { LANG_PREFIX, detectLangFromPath, DISCLOSURE_SLUG, THREAT_MAP_SLUG } from "@/lib/routes";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "pt",
  setLang: () => {},
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const detected = detectLangFromPath(location.pathname) ?? "pt";
  const [lang, setLangState] = useState<Language>(detected);

  // Keep lang in sync with URL changes
  useEffect(() => {
    const cur = detectLangFromPath(location.pathname);
    if (cur && cur !== lang) setLangState(cur);
  }, [location.pathname, lang]);

  const setLang = (newLang: Language) => {
    const from = LANG_PREFIX[lang];
    const to = LANG_PREFIX[newLang];
    let path = location.pathname;

    // Swap disclosure slug when switching language
    const oldDisc = `${from}/${DISCLOSURE_SLUG[lang]}`;
    const oldMap = `${from}/${THREAT_MAP_SLUG[lang]}`;
    if (path === oldDisc || path === `${oldDisc}/`) {
      path = `${to}/${DISCLOSURE_SLUG[newLang]}`;
    } else if (path === oldMap || path === `${oldMap}/`) {
      path = `${to}/${THREAT_MAP_SLUG[newLang]}`;
    } else if (path === from || path.startsWith(`${from}/`)) {
      path = to + path.slice(from.length);
      if (path === to) path = `${to}/`;
    } else {
      // outside a language-prefixed area (e.g. /sentinelhubguard); go to home
      path = `${to}/`;
    }

    setLangState(newLang);
    try {
      localStorage.setItem("sentinelhub-lang", newLang);
    } catch {
      /* noop */
    }
    navigate(path + location.hash);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};
